<template>
  <thead ref="tableHeader" :class="{ 'print:table-header-group': isPrintDisplayMode && breakGroupsOnNewPage }">
    <tr class="sticky top-0 z-2" :style="{ backgroundColor: headerBackgroundColor }">
      <th
        v-for="column in columns"
        :key="column.id"
        class="overflow-hidden align-middle"
        :class="{ 'cursor-pointer': column.enableSorting }"
        :style="{
          borderBottom: `1px solid ${themeVars.base}`,
          backgroundColor: headerBackgroundColor,
          ...(useFixedWidths ? { width: `${100 / columns.length}%` } : {}),
        }"
        @click="column.enableSorting ? handleSort(column.id) : undefined"
      >
        <div
          class="px-4 py-2 select-none flex justify-between items-center min-h-[40px]"
          :style="{
            backgroundColor: headerBackgroundColor,
            color: headerForegroundColor,
          }"
        >
          <span>{{ column.header }}</span>
          <template v-if="column.enableSorting && (!isPrintDisplayMode || currentSorting?.id === column.id)">
            <CIcon
              v-if="currentSorting?.id === column.id"
              class="opacity-100"
              :color="headerForegroundColor"
              :icon="currentSorting?.desc ? 'arrow-down' : 'arrow-up'"
              size="16px"
            />
            <CIcon
              v-else-if="!isPrintDisplayMode"
              class="opacity-25"
              :color="headerForegroundColor"
              icon="arrows-up-down"
              size="16px"
            />
          </template>
        </div>
      </th>
    </tr>

    <!-- Only show group header as part of the table header when breaking groups on new page -->
    <tr v-if="isPrintDisplayMode && breakGroupsOnNewPage && hasGroupHeader">
      <th
        class="font-bold text-left py-2 px-4 print:font-bold print:text-left"
        :colspan="columns.length"
        :style="{
          backgroundColor: groupHeaderBackgroundColor,
          color: groupHeaderForegroundColor,
        }"
      >
        {{ props.groupColumnHeader || columns[0]?.header }}: {{ currentGroupHeader }}
      </th>
    </tr>
  </thead>
</template>

<script setup lang="ts">
import { type ColumnSort } from '@tanstack/table-core'
import { computed, onMounted, ref } from 'vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import useCanvas from '@canvas-builder/composables/useCanvas'
import useThemeVars from '@lib/composables/useThemeVars'
import useDataTablePrint from '@canvas-builder/composables/useDataTablePrint'

interface Column {
  id: string
  header: string
  enableSorting: boolean
  sortDescFirst?: boolean
}

const props = defineProps<{
  breakGroupsOnNewPage?: boolean
  columns: Column[]
  headerBackgroundColor: string
  headerForegroundColor: string
  groupHeaderBackgroundColor?: string
  groupHeaderForegroundColor?: string
  currentSorting: ColumnSort | null
  currentGroupHeader?: string | number | boolean
  groupColumnHeader?: string
  useFixedWidths?: boolean
}>()

const emit = defineEmits<{
  sort: [columnId: string]
}>()

const { isPrintDisplayMode } = useCanvas()
const themeVars = useThemeVars()
const { applyHeaderPrintStyles } = useDataTablePrint()
const tableHeader = ref<HTMLElement | null>(null)

function handleSort(columnId: string) {
  emit('sort', columnId)
}

const hasGroupHeader = computed(() => {
  return props.currentGroupHeader !== undefined && props.currentGroupHeader !== null
})

onMounted(() => {
  if (isPrintDisplayMode.value && tableHeader.value) {
    applyHeaderPrintStyles(tableHeader.value, props.headerBackgroundColor, props.headerForegroundColor)
  }
})
</script>
